import type { HelpArticle } from "../../types";

export const memberRolePresetsArticle: HelpArticle = {
  id: "member-role-presets",
  title: "Wood member roles & presets",
  description: "Choose the wood member role that establishes a sheet's calculator, starting fields, loads, checks, and mark prefix.",
  tags: ["wood", "member role", "preset", "mark", "joist", "beam", "column"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A member role is the starting contract for a wood sheet. It chooses the calculator, seats a complete set of editable defaults, exposes the fields that apply to that role, and proposes the next mark. The preset is not an engineering selection made by Firmark: it is the named configuration you choose before checking a member.",
    },

    { kind: "heading", level: 2, text: "Choose a role in the catalog", id: "catalog" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "New calculation", "Member role presets"] },
    {
      kind: "paragraph",
      text: "The New calculation page places the nine live wood roles ahead of the general calculator catalog. Search matches only the role label and its preset note. Choosing a card creates one sheet and opens it immediately; there is no second setup dialog between the catalog and the workspace.",
    },
    {
      kind: "shot",
      shotId: "wood-role-catalog",
      caption: "The role catalog groups bending, axial, and interaction members while keeping each preset's field and check intent visible.",
    },
    {
      kind: "table",
      caption: "The live role set and the calculator each role creates.",
      headers: ["Role", "Calculator", "Preset emphasis"],
      rows: [
        ["Floor joist", "Wood beam", "Area loads, spacing, repetitive-member input, live and total deflection"],
        ["Rafter (sloped)", "Wood beam", "Slope, roof live and snow rows, spaced-member geometry"],
        ["Roof / floor beam (girder)", "Wood beam", "Line loads, built-up plies, compression-edge bracing"],
        ["Header / lintel", "Wood beam", "Short-span, multi-ply starting state and bearing"],
        ["Ridge beam", "Wood beam", "Line loads and a starting unbraced condition"],
        ["Stud (wall)", "Wood stud wall", "Axial gravity plus out-of-plane wind and interaction checks"],
        ["Post / column", "Wood column", "Axial loads, height, bearing, and effective-length input"],
        ["Beam-column (biaxial)", "Wood biaxial beam-column", "Axial load plus entered strong- and weak-axis moments"],
        ["Tension member / tie", "Wood tension", "Axial tension rows and member length"],
      ],
    },
    {
      kind: "callout",
      variant: "note",
      title: "Role names describe the input model",
      body: "The role does not prove that a member belongs in that category. It determines which calculator and fields the sheet uses. Confirm the structural model and load path outside this control.",
    },

    { kind: "heading", level: 2, text: "What the preset seats", id: "preset-contents" },
    {
      kind: "paragraph",
      text: "A preset arrives as a coherent editable state, not a handful of placeholders. Depending on the role, that state includes section source, species, grade, size, plies, span or height, spacing, bearing, slope, bracing, service condition, repetitive-member and self-weight switches, deflection criteria, and named load rows. The workspace then carries those values through the same save-and-recompute path as any later edit.",
    },
    {
      kind: "list",
      style: "unordered",
      items: [
        "**Visible fields follow the role.** A beam shows span, plies, and compression-edge bracing; a post shows height, bearing, and effective-length factor; a tension member omits bearing and bending geometry.",
        "**The check vocabulary follows the calculator.** Bending roles show flexure, shear, bearing, deflection, and stability where available. Axial and interaction roles show their own compression, tension, buckling, or combined checks.",
        "**Material families are role-scoped.** Floor joists and rafters can browse sawn lumber, I-joists, and LVL; beam roles expose the engineered beam families declared for that role. A picker choice may retarget the sheet to the calculator that owns the selected material.",
        "**Project design method is inherited.** A new sheet starts on the project's ASD or LRFD method unless the sheet later receives its own override.",
      ],
    },
    {
      kind: "shot",
      shotId: "wood-role-search",
      caption: "Search narrows the role cards by their real labels and preset notes; the Post / column result matches its pure axial note.",
    },

    { kind: "heading", level: 2, text: "Marks are proposed by role", id: "marks" },
    {
      kind: "paragraph",
      text: "Firmark proposes the next mark from a role-specific prefix: FJ, R, B, H, RB, ST, P, BC, or T. The counter reads existing sheet names with that prefix and role mapping, so a rafter mark does not inflate the ridge-beam sequence. The proposed mark is still editable in the sheet header and is persisted as the calculation name. After creation, the header keeps both the stored mark and role visible while the member is edited.",
    },
    {
      kind: "values",
      caption: "Examples from the role-prefix map.",
      rows: [
        { label: "Floor joist", value: "FJ-1, FJ-2, FJ-3…" },
        { label: "Rafter / ridge beam", value: "R-1… / RB-1… — separate sequences" },
        { label: "Stud / post / beam-column", value: "ST-1… / P-1… / BC-1…" },
        { label: "Header / tension", value: "H-1… / T-1…" },
      ],
    },

    { kind: "heading", level: 2, text: "Changing the role on an existing sheet", id: "change-role" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "H-3", "Member role"] },
    {
      kind: "paragraph",
      text: "The header's Member role control offers only roles backed by the same calculator. On a wood-beam sheet, for example, you can move among floor joist, rafter, beam, header, and ridge. A post cannot be changed into a beam in place because that would cross calculator contracts; create the other role as a new sheet instead.",
    },
    {
      kind: "paragraph",
      text: "An accepted in-place role change preserves the current mark, then reseeds the rest of the sheet from the destination preset and recomputes it. That includes geometry, loads, material defaults, conditions, and serviceability values. Treat the action as replacing the sheet's setup, not as changing a display label around the values already entered.",
    },
    {
      kind: "shot",
      shotId: "wood-role-header",
      caption: "At the laptop-width sheet layout, H-3's header keeps the mark separate from the role master-control while the optional preset note yields to the working controls.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Review every field after a role change",
      body: "Role switching deliberately restores the destination preset's defaults while keeping the mark. It does not translate custom geometry or loads from the previous role. Read cards A, B, and C before relying on the recomputed result.",
    },

    { kind: "related", topics: ["member-geometry-supports", "member-loads", "design-conditions", "member-picker"] },
  ],
};
